import type { TelemetryRuntimeConfig } from "@/lib/types";

const REDIRECT_GUARD_KEY = "w7s.logs-ui.auth.redirect.ts";
const REDIRECT_GUARD_WINDOW_MS = 10_000;

const readErrorMessage = (error: unknown): string => {
  if (error instanceof Error) return error.message;
  return String(error ?? "");
};

export const isUnauthenticatedTelemetryError = (error: unknown): boolean => {
  const message = readErrorMessage(error).toLowerCase();
  return (
    message.includes("missing session token") ||
    message.includes("invalid or expired session token")
  );
};

export const resolveAccountOrigin = (telemetryOrigin: string): string => {
  try {
    const host = new URL(telemetryOrigin).host.toLowerCase();
    if (host.endsWith(".w7s.io")) return "https://account.w7s.io";
  } catch {
    // Ignore URL parse issues and fall back below.
  }
  return "https://account.w7s.cloud";
};

export const shouldAutoRedirectToLogin = (
  config: TelemetryRuntimeConfig,
  errors: unknown[],
): boolean => {
  if (config.authToken) return false;
  return errors.some((error) => isUnauthenticatedTelemetryError(error));
};

export const canAttemptAuthRedirectNow = (): boolean => {
  const now = Date.now();
  const previousRaw = window.sessionStorage.getItem(REDIRECT_GUARD_KEY) ?? "";
  const previous = Number.parseInt(previousRaw, 10);
  if (Number.isFinite(previous) && now - previous < REDIRECT_GUARD_WINDOW_MS) {
    return false;
  }
  window.sessionStorage.setItem(REDIRECT_GUARD_KEY, String(now));
  return true;
};

export const buildAccountGithubLoginUrl = (accountOrigin: string, redirectUrl: string): string =>
  `${accountOrigin}/github/auth?redirect_url=${encodeURIComponent(redirectUrl)}`;
